import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCreateTimeEntry } from "@/hooks/useTimeEntries";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";
import { CategorySelect } from "@/components/timer/CategorySelect";
import type { Database } from "@/integrations/supabase/types";

type TimeCategory = Database["public"]["Enums"]["time_category"];

interface ProjectManualTimeEntryProps {
  projectId: string;
  projectHourlyRate?: number | null;
}

export function ProjectManualTimeEntry({ projectId, projectHourlyRate }: ProjectManualTimeEntryProps) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const createTimeEntry = useCreateTimeEntry();

  const [isExpanded, setIsExpanded] = useState(false);
  const [category, setCategory] = useState<TimeCategory>("dev");
  const [customCategory, setCustomCategory] = useState<string>("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState<Date>(new Date());
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [isBillable, setIsBillable] = useState(true);
  const [hourlyRate, setHourlyRate] = useState("");

  const resetForm = () => {
    setCategory("dev");
    setCustomCategory("");
    setDescription("");
    setDate(new Date());
    setStartTime("09:00");
    setEndTime("10:00");
    setIsBillable(true);
    setHourlyRate("");
  };

  const handleSave = async () => {
    if (!user?.id) return;

    const dateStr = format(date, "yyyy-MM-dd");
    const newStartTime = new Date(`${dateStr}T${startTime}:00`);
    const newEndTime = new Date(`${dateStr}T${endTime}:00`);

    if (newEndTime <= newStartTime) {
      toast.error("O horário de fim deve ser maior que o de início.");
      return;
    }

    const durationMinutes = Math.round((newEndTime.getTime() - newStartTime.getTime()) / 60000);

    if (durationMinutes > 24 * 60) {
      toast.error("Duração máxima de 24 horas por entrada.");
      return;
    }

    const effectiveRate = hourlyRate 
      ? parseFloat(hourlyRate) 
      : projectHourlyRate || profile?.hourly_rate || null;

    try {
      await createTimeEntry.mutateAsync({
        user_id: user.id,
        project_id: projectId,
        category,
        custom_category: customCategory.trim() || null,
        description: description.trim() || null,
        start_time: newStartTime.toISOString(),
        end_time: newEndTime.toISOString(),
        duration_minutes: durationMinutes,
        is_billable: isBillable,
        hourly_rate: effectiveRate,
      });
      toast.success("Entrada registrada com sucesso!");
      resetForm();
      setIsExpanded(false);
    } catch (error) {
      toast.error("Não foi possível criar a entrada.");
    }
  };

  const calculateDuration = () => {
    if (!startTime || !endTime) return "-";
    const [sh, sm] = startTime.split(":").map(Number);
    const [eh, em] = endTime.split(":").map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;
    if (endMins <= startMins) return "-";
    const diff = endMins - startMins;
    const h = Math.floor(diff / 60);
    const m = diff % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  if (!isExpanded) {
    return (
      <Button
        variant="outline"
        onClick={() => setIsExpanded(true)}
        className="gap-2"
      >
        <Plus className="h-4 w-4" />
        Adicionar Entrada Manual
      </Button>
    );
  }

  return (
    <Card className="border-dashed">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Nova Entrada Manual</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              resetForm();
              setIsExpanded(false);
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Categoria</Label>
            <CategorySelect
              value={category}
              customCategory={customCategory}
              onCategoryChange={setCategory}
              onCustomCategoryChange={setCustomCategory}
            />
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="O que você fez?"
            />
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label>Data</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start gap-2">
                  <CalendarDays className="h-4 w-4" />
                  {format(date, "dd/MM/yyyy", { locale: ptBR })}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => d && setDate(d)}
                  locale={ptBR}
                  className="pointer-events-auto"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Início</Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Fim</Label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="billable-project"
                checked={isBillable}
                onCheckedChange={(checked) => setIsBillable(checked as boolean)}
              />
              <Label htmlFor="billable-project" className="cursor-pointer text-sm">
                Faturável
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">R$/h:</Label>
              <Input
                type="number"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                placeholder={String(projectHourlyRate || profile?.hourly_rate || 0)}
                className="w-24"
              />
            </div>
          </div>

          <span className="text-sm text-muted-foreground">
            Duração: <strong className="text-foreground">{calculateDuration()}</strong>
          </span>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={() => {
              resetForm();
              setIsExpanded(false);
            }}
          >
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={createTimeEntry.isPending}>
            {createTimeEntry.isPending ? "Salvando..." : "Adicionar"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
